/* eslint-disable max-lines */
import { RETURN_OFFER } from 'analytics/constants/ids';
import { cancelFlow_cancelResultClick, cancelFlow_cancelResultScreenLoad } from 'analytics/trackers/cancelFlow';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ModalName } from 'store/modals/types';
import {
    cancelSubscription,
    fetchDiscountSubscriptions,
    sendFeedback,
    setCancellationCandidate,
} from 'store/subscriptions/actions';
import { checkBeforeFetchingDiscount } from 'store/subscriptions/helpers';

import { DISCOUNT_TYPES } from 'constants/subscriptions';

import { useDispatch } from 'hooks';

import { IUserFeedback } from 'components/Modals/CancellationReasonModal/types';

import { ISubscription } from 'types/subscription';

import { useModals } from '../modals';

import { checkIsPaymentFormNeeded, isOneYearSubscription } from './helpers';
import { useCommonSubscriptionLogic } from './index';

const useCaliforniaFlow = (subs: ISubscription[]) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { closeModal, openModal } = useModals();
    const { displaySurveyModal, displayCancelProcessModal, displayCancellingIssue, displayReturnOffer } =
        useCommonSubscriptionLogic(subs);

    const cancellationCandidateRef = useRef<ISubscription | null>(null);

    const initCancellationForCalifornia = (candidate: ISubscription) => {
        dispatch(setCancellationCandidate(candidate));
        cancellationCandidateRef.current = candidate;

        if (candidate.fee?.fee_enabled) {
            openModal(checkIsPaymentFormNeeded(candidate) ? ModalName.feePayment : ModalName.feeAlert);

            return;
        }

        onCancelSubscription();
    };

    const handleSubscriptionCanceledPopupClosed = (candidate: ISubscription, action: 'got_it' | 'close') => {
        cancelFlow_cancelResultClick({ action, result: 'success', subscription_id: candidate.external_id });
        handleSurveyModal(candidate);
    };

    const onCancelSubscription = () => {
        const candidate = cancellationCandidateRef.current as ISubscription;

        if (!candidate) return closeModal();

        displayCancelProcessModal(candidate);

        dispatch(
            cancelSubscription({
                id: candidate.external_id,
                provider: candidate.payment_provider,
                onError: () => {
                    displayCancellingIssue(onCancelSubscription, candidate.external_id);
                },
                onSuccess: () => {
                    cancelFlow_cancelResultScreenLoad({ result: 'success', subscription_id: candidate.external_id });

                    openModal(ModalName.universalModal, {
                        onClose: () => handleSubscriptionCanceledPopupClosed(candidate, 'close'),
                        onPrimaryClick: () => handleSubscriptionCanceledPopupClosed(candidate, 'got_it'),
                        withCloseButton: true,
                        captions: {
                            title: t('subscription.cancel.cancelModal.title'),
                            extraText: {
                                text: t('subscription.cancel.cancelModal.subtitle'),
                            },
                            primaryButton: t('basics.gotIt'),
                        },
                        dataLocators: {
                            modal: 'cancelSubModal',
                            container: 'cancelSubscriptionPlanModal',
                            primaryButton: 'cancelSubscriptionPlanContinueButton',
                            closeButton: 'cancelSubscriptionPlanCloseButton',
                        },
                    });
                },
                onCancel() {
                    closeModal();

                    cancelFlow_cancelResultScreenLoad({
                        subscription_id: candidate.external_id,
                        result: 'fail',
                    });
                },
            })
        );
    };

    const handleSurveyModal = (candidate: ISubscription) => {
        const hideSurvey = isOneYearSubscription(candidate.subscription_period) || candidate.is_promo_code_used;
        const isOfferAlreadyApplied = Boolean(candidate?.discount.discount_type === DISCOUNT_TYPES.extra);

        if (hideSurvey || isOfferAlreadyApplied) {
            closeModal();

            return;
        }

        displaySurveyModal(onSubmitSurvey, onCancelSubscription, RETURN_OFFER);
        const shouldProceedWithFetch = checkBeforeFetchingDiscount(candidate);

        if (shouldProceedWithFetch) {
            dispatch(
                fetchDiscountSubscriptions({
                    discount_type: DISCOUNT_TYPES.extra,
                    id: candidate?.external_id,
                    provider: candidate?.payment_provider,
                    onError: () => {},
                    onSuccess: () => {},
                })
            );
        }
    };

    const onSubmitSurvey = ({ reason }: IUserFeedback) => {
        const candidate = cancellationCandidateRef.current as ISubscription;

        const theReason = reason ? reason.shortKey : '';

        dispatch(
            sendFeedback({
                reason: theReason,
            })
        );

        displayReturnOffer(candidate);
    };

    return {
        initCancellationForCalifornia,
    };
};

export default useCaliforniaFlow;
